import { useCallback, useEffect, useMemo, useState } from 'react';
import { collection, deleteField, doc, getDoc, getDocs, orderBy, query, setDoc } from 'firebase/firestore';
import { db } from '@/firebase/firebase';
import { useAuthStore } from '@/stores/authStore';
import useAlertStore from '@/stores/alertStore';
import { errorReporter } from '@/services/errorReportingService';

export interface GeminiModelOption {
  id: string; // Firebase document ID
  name: string; // model_name field value (used for backend AI processing)
  description?: string;
}

interface UseUserModelSelectionResult {
  models: GeminiModelOption[];
  isLoadingModels: boolean;
  selectedModelId: string; // Actually contains model NAME (not ID) for backend compatibility
  isSavingSelection: boolean;
  onSelectModel: (modelId: string) => Promise<void>; // modelId param is actually model NAME
}

const MODELS_COLLECTION = 'models';
const USERS_COLLECTION = 'users';

const sanitizeModelDocument = (docId: string, data: Record<string, unknown>): GeminiModelOption => {
  const rawName = data?.model_name;
  const rawDescription = data?.description;

  const name = typeof rawName === 'string' && rawName.trim().length > 0 ? rawName.trim() : docId;
  const description = typeof rawDescription === 'string' ? rawDescription : undefined;

  return {
    id: docId,
    name,
    description,
  };
};

const extractUserModelId = (rawValue: unknown): string => {
  if (typeof rawValue === 'string') {
    return rawValue;
  }

  if (rawValue && typeof rawValue === 'object') {
    const candidate = (rawValue as Record<string, unknown>).id;
    if (typeof candidate === 'string') {
      return candidate;
    }
  }

  return '';
};

export const useUserModelSelection = (): UseUserModelSelectionResult => {
  const { userData } = useAuthStore();
  const showAlert = useAlertStore((state) => state.showAlert);

  const [models, setModels] = useState<GeminiModelOption[]>([]);
  const [isLoadingModels, setIsLoadingModels] = useState<boolean>(true);
  const [selectedModelId, setSelectedModelId] = useState<string>('');
  const [isSavingSelection, setIsSavingSelection] = useState<boolean>(false);

  useEffect(() => {
    let isMounted = true;

    const fetchModels = async () => {
      setIsLoadingModels(true);

      try {
        const modelsQuery = query(collection(db, MODELS_COLLECTION), orderBy('model_name'));
        const snapshot = await getDocs(modelsQuery);
        if (!isMounted) {
          return;
        }

        const parsedModels = snapshot.docs.map((document) =>
          sanitizeModelDocument(document.id, document.data() as Record<string, unknown>),
        );

        setModels(parsedModels);
      } catch (error) {
        errorReporter.expected('Failed to load models from Firebase', error, {
          feature: 'settings',
          operation: 'loadModels',
        });
        if (isMounted) {
          setModels([]);
        }
      } finally {
        if (isMounted) {
          setIsLoadingModels(false);
        }
      }
    };

    fetchModels();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const fetchUserModelSelection = async () => {
      if (!userData?.id) {
        setSelectedModelId('');
        return;
      }

      try {
        const userDocRef = doc(collection(db, USERS_COLLECTION), userData.id);
        const userDoc = await getDoc(userDocRef);
        if (!isMounted) {
          return;
        }

        if (userDoc.exists()) {
          const rawUserModel = userDoc.data()?.model_version;
          const userModelId = extractUserModelId(rawUserModel);
          setSelectedModelId(userModelId);
        } else {
          setSelectedModelId('');
        }
      } catch (error) {
        errorReporter.expected('Failed to read user model selection from Firebase', error, {
          feature: 'settings',
          operation: 'readModelSelection',
        });
        if (isMounted) {
          setSelectedModelId('');
        }
      }
    };

    fetchUserModelSelection();

    return () => {
      isMounted = false;
    };
  }, [userData?.id]);

  const onSelectModel = useCallback(
    async (modelId: string) => {
      if (!userData?.id) {
        showAlert('You need to be signed in to change your custom model.', 'error');
        return;
      }

      if (modelId === selectedModelId) {
        return;
      }

      setIsSavingSelection(true);

      try {
        const userDocRef = doc(collection(db, USERS_COLLECTION), userData.id);

        if (modelId) {
          await setDoc(
            userDocRef,
            {
              model_version: modelId,
            },
            { merge: true },
          );
        } else {
          await setDoc(
            userDocRef,
            {
              model_version: deleteField(),
            },
            { merge: true },
          );
        }

        setSelectedModelId(modelId);
        showAlert('Your custom model has been updated.', 'success');
      } catch (error) {
        errorReporter.expected('Failed to update user model selection in Firebase', error, {
          feature: 'settings',
          operation: 'updateModelSelection',
        });
        showAlert('We could not update your custom model. Please try again.', 'error');
      } finally {
        setIsSavingSelection(false);
      }
    },
    [selectedModelId, showAlert, userData?.id],
  );

  return useMemo(
    () => ({
      models,
      isLoadingModels,
      selectedModelId,
      isSavingSelection,
      onSelectModel,
    }),
    [isLoadingModels, isSavingSelection, models, onSelectModel, selectedModelId],
  );
};

export default useUserModelSelection;
